import { useEffect, useMemo } from 'react';
import type { StudioApi } from '../../engine/api';
import type { MutateOptions } from '../../state/api';
import { attempt } from '../safe';

export interface EditSession {
  /**
   * Applies one live change. `read` returns the value currently in the document
   * and `write` stores one; both address the same thing, named by `key`.
   */
  edit<T>(key: string, read: () => T, write: (value: T, options?: MutateOptions) => void, value: T): void;
  /** Records everything edited since the last commit as a single undo step. */
  commit(): void;
}

interface Pending {
  start: unknown;
  read(): unknown;
  write(value: unknown, options?: MutateOptions): void;
}

let serial = 0;

/**
 * Typing in a field or dragging a slider changes the document many times. The
 * store merges such changes only while they arrive without a pause, so here
 * they are applied without history and `commit` (blur, Enter, pointer release,
 * dialog close) rewinds them and records the net change once.
 */
export function createEditSession(studio: Pick<StudioApi, 'project' | 'notify'>): EditSession {
  const pending = new Map<string, Pending>();
  return {
    edit(key, read, write, value) {
      attempt(studio, () => {
        if (!pending.has(key)) pending.set(key, { start: read(), read, write: write as Pending['write'] });
        write(value, { undoable: false });
      });
    },
    commit() {
      if (pending.size === 0) return;
      const entries = [...pending.values()];
      pending.clear();
      const coalesceKey = `edit:${++serial}`;
      attempt(studio, () => {
        for (const entry of entries) {
          const end = entry.read();
          if (JSON.stringify(end) === JSON.stringify(entry.start)) continue;
          entry.write(entry.start, { undoable: false });
          entry.write(end, { coalesceKey });
        }
      });
      studio.project.sealHistory();
    },
  };
}

/** An edit session that commits whatever is still open when the component goes away. */
export function useEditSession(studio: Pick<StudioApi, 'project' | 'notify'>): EditSession {
  const session = useMemo(() => createEditSession(studio), [studio]);
  useEffect(() => () => session.commit(), [session]);
  return session;
}
